import React from 'react';
import { Card, Label, Icon, Button, Image } from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FeedTournament } from '../../app/models/Playground';

interface Props {
  tournament: FeedTournament;
}

export function TournamentFeedCard({ tournament }: Props) {
  const { t } = useTranslation();

  const spotsLeft = tournament.maxTeams - tournament.registeredTeamsCount;
  const isFull = spotsLeft <= 0;
  const registrationClosesAt = new Date(tournament.registrationClosesAt);
  const startsAt = new Date(tournament.startsAt);

  // Color based on spots remaining
  const spotsColor: 'red' | 'orange' | 'green' =
    isFull ? 'red' : spotsLeft <= 3 ? 'orange' : 'green';

  // Format for tournament format display
  const formatLabel = tournament.format
    .replace(/([A-Z])/g, ' $1')
    .trim();

  return (
    <Card
      fluid
      style={{
        borderRadius: 12,
        borderTop: '3px solid #6366f1',   // indigo accent — distinct from games
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <Card.Content>

        {/* ── Header row: logo + name + status badge ── */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
          {tournament.logoUrl ? (
            <Image
              src={tournament.logoUrl}
              style={{
                width: 44, height: 44, borderRadius: 8,
                objectFit: 'cover', flexShrink: 0,
                border: '1px solid #e8eaf6',
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div style={{
              width: 44, height: 44, borderRadius: 8, flexShrink: 0,
              background: '#eef2ff', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name='trophy' color='violet' size='large' style={{ margin: 0 }} />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 700, fontSize: '0.97em', color: '#1a1a2e',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {tournament.name}
            </div>
            <div style={{ fontSize: '0.78em', color: '#555', marginTop: 2 }}>
              <Icon name='soccer' color='grey' size='small' />
              {tournament.sportName}
              <span style={{ margin: '0 6px', color: '#ccc' }}>·</span>
              <Icon name='sitemap' color='grey' size='small' />
              {formatLabel}
            </div>
          </div>

          <Label
            size='mini'
            color='violet'
            style={{ borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <Icon name='lock open' />
            {t('playground.tournament.registrationOpen')}
          </Label>
        </div>

        {/* ── Info row: organizer + distance + online ── */}
        <div style={{
          display: 'flex', gap: 12, flexWrap: 'wrap',
          fontSize: '0.82em', color: '#555', marginBottom: 8,
        }}>
          <span>
            <Icon name='user outline' color='grey' size='small' />
            {t('playground.tournament.organizer')}: <strong>{tournament.organizerName}</strong>
          </span>

          {tournament.isOnline ? (
            <span>
              <Icon name='wifi' color='teal' size='small' />
              {t('playground.tournament.online')}
            </span>
          ) : tournament.distanceKm != null ? (
            <span>
              <Icon name='map marker alternate' color='teal' size='small' />
              {tournament.distanceKm.toFixed(1)} km
            </span>
          ) : null}
        </div>

        {/* ── Dates row ── */}
        <div style={{
          display: 'flex', gap: 14, flexWrap: 'wrap',
          fontSize: '0.8em', color: '#666', marginBottom: 8,
        }}>
          <span>
            <Icon name='calendar check outline' color='green' size='small' />
            {t('playground.tournament.closesOn', {
              date: format(registrationClosesAt, 'dd MMM yyyy'),
            })}
          </span>
          <span>
            <Icon name='flag' color='blue' size='small' />
            {t('playground.tournament.startsOn', {
              date: format(startsAt, 'dd MMM yyyy'),
            })}
          </span>
        </div>

        {/* ── Teams + entry fee row ── */}
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap',
          alignItems: 'center', marginBottom: 4,
        }}>
          <Label
            size='mini'
            color={spotsColor}
            basic={!isFull}
            style={{ borderRadius: 20 }}
          >
            <Icon name='users' />
            {t('playground.tournament.teams', {
              registered: tournament.registeredTeamsCount,
              max: tournament.maxTeams,
            })}
          </Label>

          {tournament.entryFee != null && tournament.entryFee > 0 ? (
            <Label size='mini' basic color='grey' style={{ borderRadius: 20 }}>
              <Icon name='money bill alternate outline' />
              {t('playground.tournament.entryFee', {
                fee: `${tournament.entryFee}`,
              })}
            </Label>
          ) : (
            <Label size='mini' basic color='green' style={{ borderRadius: 20 }}>
              <Icon name='check circle outline' />
              {t('playground.tournament.free')}
            </Label>
          )}

          {tournament.prizePool && (
            <Label size='mini' basic color='yellow' style={{ borderRadius: 20 }}>
              <Icon name='trophy' />
              {t('playground.tournament.prizePool', { prize: tournament.prizePool })}
            </Label>
          )}
        </div>

      </Card.Content>

      {/* ── Footer: action buttons ── */}
      <Card.Content
        extra
        style={{ background: '#fafafa', paddingTop: 8, paddingBottom: 8 }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button
            as={NavLink}
            to={`/tournaments/${tournament.id}`}
            basic
            size='mini'
            color='violet'
            style={{ borderRadius: 20 }}
          >
            <Icon name='info circle' />
            {t('playground.tournament.viewDetails')}
          </Button>

          {!isFull && (
            <Button
              as={NavLink}
              to={`/tournaments/${tournament.id}`}
              color='violet'
              size='mini'
              style={{ borderRadius: 20 }}
            >
              <Icon name='sign in' />
              {t('playground.tournament.register')}
            </Button>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}